import React, { useCallback, useState } from 'react';

import { Icon } from '../../../../../../components/Icon';
import { IExerciseMuscleGroup } from '../../../../../../domains/Exercise';
import { api } from '../../../../../../services/api';
import { useToast } from '../../../../../../hooks/toast';
import { colors } from '../../../../../../styles/tokens';

import {
  AccentBar,
  Container,
  Content,
  DragHandle,
  ExerciseSubtitle,
  ExerciseTitle,
  FieldGroup,
  FieldInput,
  FieldLabel,
  FieldsGroup,
  InfoGroup,
  MainRow,
  NotesInput,
  RemoveButton,
} from './styles';

interface TrainingCardProps {
  id: string;
  exerciseName: string;
  muscleGroups: IExerciseMuscleGroup[];
  description?: string;
  thumbnail_url?: string;
  // `| null` porque registros antigos podem ter dado corrompido na API.
  sets_min?: number | null;
  sets_max?: number | null;
  reps_min?: number | null;
  reps_max?: number | null;
  interval_min?: number | null;
  interval_max?: number | null;
  additional_information?: string;
  functionChangeButton: () => void;
  functionRemoveButton: () => void;
}

interface IPrescription {
  sets_min?: number;
  sets_max?: number;
  reps_min?: number;
  reps_max?: number;
  interval_min?: number;
  interval_max?: number;
  additional_information?: string;
}

// Alguns registros antigos têm `null`/`NaN` gravado em interval/sets/reps
// (dado legado corrompido -- o Postgres aceita `NaN` em coluna `numeric`).
// Sem isso, esses valores aparecem como o texto literal "null"/"NaN" no
// campo e, pior, voltam a ser reenviados como estão em qualquer save
// (mesmo editando um campo diferente), e o backend rejeita com "must be a
// number".
const sanitizeNumber = (value: unknown): number | undefined => {
  if (typeof value !== 'number' || Number.isNaN(value)) return undefined;
  return value;
};

const formatRange = (
  minInput?: number | null,
  maxInput?: number | null,
  suffix = '',
): string => {
  const min = sanitizeNumber(minInput);
  const max = sanitizeNumber(maxInput);

  if (min === undefined && max === undefined) return '';
  if (max === undefined || min === max) return `${min}${suffix}`;
  return `${min}-${max}${suffix}`;
};

const parseRange = (value: string): { min?: number; max?: number } => {
  const digitsOnly = value.replace(/[^\d-]/g, '');

  if (!digitsOnly) return { min: undefined, max: undefined };

  if (digitsOnly.includes('-')) {
    const [min, max] = digitsOnly.split('-').map(part => Number(part));
    return {
      min: Number.isNaN(min) ? undefined : min,
      max: Number.isNaN(max) ? undefined : max,
    };
  }

  const single = Number(digitsOnly);
  return { min: single, max: single };
};

const TrainingCard: React.FC<TrainingCardProps> = ({
  id,
  exerciseName,
  muscleGroups,
  description,
  thumbnail_url,
  functionChangeButton,
  functionRemoveButton,
  ...prescriptionProps
}) => {
  const { addToast } = useToast();
  const [prescription, setPrescription] = useState<IPrescription>({
    sets_min: sanitizeNumber(prescriptionProps.sets_min),
    sets_max: sanitizeNumber(prescriptionProps.sets_max),
    reps_min: sanitizeNumber(prescriptionProps.reps_min),
    reps_max: sanitizeNumber(prescriptionProps.reps_max),
    interval_min: sanitizeNumber(prescriptionProps.interval_min),
    interval_max: sanitizeNumber(prescriptionProps.interval_max),
    additional_information: prescriptionProps.additional_information,
  });

  const mainGroup =
    muscleGroups.find(group => group.is_main) || muscleGroups[0];
  const mainGroupName = mainGroup?.muscle_group?.name || mainGroup?.name;

  const handleSave = useCallback(
    async (nextPrescription: IPrescription) => {
      // Sanitiza de novo na saída -- `nextPrescription` normalmente já vem
      // limpo (parseRange/prescription state nunca produzem null/NaN), mas
      // isso é a última barreira antes de sair pro backend, caso algum
      // campo não editado ainda carregue dado legado corrompido.
      const sanitized: IPrescription = {
        sets_min: sanitizeNumber(nextPrescription.sets_min),
        sets_max: sanitizeNumber(nextPrescription.sets_max),
        reps_min: sanitizeNumber(nextPrescription.reps_min),
        reps_max: sanitizeNumber(nextPrescription.reps_max),
        interval_min: sanitizeNumber(nextPrescription.interval_min),
        interval_max: sanitizeNumber(nextPrescription.interval_max),
        additional_information: nextPrescription.additional_information,
      };

      try {
        await api.put(`trainings/${id}`, sanitized);
      } catch {
        addToast({
          type: 'error',
          title: 'Erro ao salvar os dados do exercício.',
          description: 'Tente novamente mais tarde.',
        });
      }
    },
    [addToast, id],
  );

  const handleBlurRange = useCallback(
    (
      value: string,
      minField: keyof IPrescription,
      maxField: keyof IPrescription,
    ) => {
      const { min, max } = parseRange(value);

      const next = { ...prescription, [minField]: min, [maxField]: max };
      setPrescription(next);
      handleSave(next);
    },
    [handleSave, prescription],
  );

  const handleBlurNotes = useCallback(
    (value: string) => {
      const next = { ...prescription, additional_information: value };
      setPrescription(next);
      handleSave(next);
    },
    [handleSave, prescription],
  );

  return (
    <Container>
      <AccentBar />
      <Content>
        <MainRow>
          <InfoGroup>
            <DragHandle>
              <Icon
                name="drag_indicator"
                size={20}
                color={colors.onSurfaceVariant}
              />
            </DragHandle>

            <button
              type="button"
              onClick={functionChangeButton}
              style={{
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                textAlign: 'left',
              }}
            >
              <ExerciseTitle>{exerciseName}</ExerciseTitle>
              {!!mainGroupName && (
                <ExerciseSubtitle>{mainGroupName}</ExerciseSubtitle>
              )}
            </button>
          </InfoGroup>

          <FieldsGroup>
            <FieldGroup>
              <FieldLabel>Séries</FieldLabel>
              <FieldInput
                type="text"
                width={48}
                defaultValue={formatRange(
                  prescription.sets_min,
                  prescription.sets_max,
                )}
                onBlur={e =>
                  handleBlurRange(e.target.value, 'sets_min', 'sets_max')
                }
              />
            </FieldGroup>

            <FieldGroup>
              <FieldLabel>Reps</FieldLabel>
              <FieldInput
                type="text"
                defaultValue={formatRange(
                  prescription.reps_min,
                  prescription.reps_max,
                )}
                onBlur={e =>
                  handleBlurRange(e.target.value, 'reps_min', 'reps_max')
                }
              />
            </FieldGroup>

            <FieldGroup>
              <FieldLabel>Descanso</FieldLabel>
              <FieldInput
                type="text"
                defaultValue={formatRange(
                  prescription.interval_min,
                  prescription.interval_max,
                  's',
                )}
                onBlur={e =>
                  handleBlurRange(
                    e.target.value,
                    'interval_min',
                    'interval_max',
                  )
                }
              />
            </FieldGroup>

            <RemoveButton type="button" onClick={functionRemoveButton}>
              <Icon name="delete_outline" size={20} color="currentColor" />
            </RemoveButton>
          </FieldsGroup>
        </MainRow>

        <NotesInput
          placeholder="Adicione observações para o aluno sobre este exercício..."
          defaultValue={prescription.additional_information ?? ''}
          onBlur={e => handleBlurNotes(e.target.value)}
        />
      </Content>
    </Container>
  );
};

export { TrainingCard };
